import { useEffect, useState } from "react";
import HttpError from "@/lib/api/httpError";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";

import { useMakePayment } from "@/mutations/opportunity/make-payment";
import { useProfileQuery } from "@/queries/user/profile";

import { useReqType } from "@/stores/req-type";
import { usePaymentInfoDialogState } from "@/components/dialogs/payment-info/store";

import {
  Billing,
  CandidateJobOpening,
  CompanyJobOpening,
  Offer,
} from "@/types";

interface PaymentButtonBaseProps {
  opportunity: CompanyJobOpening | CandidateJobOpening;
  oldButtonType?: boolean;
  closeDialogFn?: () => void;
}

type PaymentButtonProps = PaymentButtonBaseProps &
  (
    | {
        offer: Offer;
        billing?: never;
      }
    | {
        billing: Billing;
        offer?: never;
      }
  );

const PaymentButton = ({
  opportunity,
  offer,
  billing,
  oldButtonType,
  closeDialogFn,
}: PaymentButtonProps) => {
  const [openErrorDialog, setOpenErrorDialog] = useState(false);

  const switchValue = useReqType((s) => s.switch);
  const { profile } = useProfileQuery();
  const { makePayment, isMakingPayment, makePaymentError } = useMakePayment();

  useEffect(() => {
    if (makePaymentError instanceof HttpError) {
      if (makePaymentError.errorType === "MISSED_CHECKOUT_WINDOW") {
        setOpenErrorDialog(true);
      }
    }
  }, [makePaymentError]);

  if (
    switchValue !== "company" ||
    profile?.companyAccounts[0]?.accountType !== "Admin"
  ) {
    return undefined;
  }

  if (openErrorDialog) {
    return (
      <Dialog open={openErrorDialog} onOpenChange={setOpenErrorDialog}>
        <DialogContent className="p-0">
          <DialogHeader className="p-6">
            <div className="flex items-center gap-2">
              <DialogTitle>Checkout window has expired</DialogTitle>
            </div>
            <DialogDescription className="sr-only">
              An issue occured when attempting to checkout
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[calc(100vh-10rem)] md:max-h-[70vh] px-6 pb-6">
            <div className="flex flex-col gap-2">
              <p className="text-base text-brand-neutral-black">
                Your scheduled start date has already passed, so we’re unable to
                generate a checkout link at this time.
              </p>
              <p className="text-base text-brand-neutral-black">
                Our team has been notified and will follow up shortly to help
                you get set up and ensure everything stays on track.
              </p>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    );
  }

  if (billing) {
    if (
      billing.companyCheckoutStatus === "completed" ||
      billing.companyCheckoutStatus === "active" ||
      billing.companyCheckoutStatus === "cancelled" ||
      billing.terms[0]?.termType !== "recurring"
    )
      return;

    if (billing?.candidateReady) {
      return (
        <Button
          variant={oldButtonType ? "secondary" : "success"}
          type="submit"
          disabled={isMakingPayment}
          onClick={() => {
            makePayment({
              openingId: opportunity.id,
              billingId: billing.id,
            });
          }}
        >
          Finalize Payment
        </Button>
      );
    } else {
      return (
        <Button
          variant={oldButtonType ? "secondary" : "success"}
          type="submit"
          onClick={() => {
            closeDialogFn?.();
            usePaymentInfoDialogState.getState().setOpen(true);
          }}
        >
          Add Payment Method
        </Button>
      );
    }
  } else if (
    (offer.status === "sent" && offer.sender === "company") ||
    offer.status === "accepted"
  ) {
    return (
      <Button
        variant={oldButtonType ? "secondary" : "success"}
        type="submit"
        onClick={() => {
          closeDialogFn?.();
          usePaymentInfoDialogState.getState().setOpen(true);
        }}
      >
        Add Payment Method
      </Button>
    );
  }

  return undefined;
};

export default PaymentButton;
